import { paintWordmark } from "./logo";

const VERT = `
attribute vec2 p;
varying vec2 uv;
void main() { uv = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }`;

const SIM = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 uv;
uniform sampler2D state;
uniform vec2 texel;
uniform vec4 drops[8];
uniform int count;
uniform float aspect;
void main() {
  vec4 c = texture2D(state, uv);
  float avg = 0.25 * (
    texture2D(state, uv + vec2(texel.x, 0.0)).r + texture2D(state, uv - vec2(texel.x, 0.0)).r +
    texture2D(state, uv + vec2(0.0, texel.y)).r + texture2D(state, uv - vec2(0.0, texel.y)).r);
  c.g += (avg - c.r) * 0.9;
  c.g *= 0.992;
  c.r += c.g;
  c.r *= 0.998;
  for (int i = 0; i < 8; i++) {
    if (i >= count) break;
    vec4 drop = drops[i];
    float k = 0.0;
    if (drop.w < 0.0) {

      float dl = abs(uv.y - drop.y) / -drop.w;
      float rough = 0.6 + 0.4 * sin(uv.x * aspect * 5.0 + drop.x * 6.2832) * sin(uv.x * aspect * 2.3 - drop.x * 4.0);
      if (dl < 1.0) k = rough * (0.5 + 0.5 * cos(3.14159265 * dl));
    } else {
      float dd = length((uv - drop.xy) * vec2(aspect, 1.0)) / drop.w;
      if (dd < 1.0) k = 0.5 + 0.5 * cos(3.14159265 * dd);
    }
    c.r += drop.z * k;
  }
  gl_FragColor = c;
}`;

const VIEW = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 uv;
uniform sampler2D state;
uniform vec2 texel;
uniform float time;
uniform float tilt;
uniform float aspect;
uniform vec3 blue;
uniform vec3 inkColor;
uniform float glow;
uniform vec4 view;
uniform vec2 fade;
uniform float scale;
uniform vec4 vortex;
uniform vec2 vortexSize;
uniform sampler2D nameTex;
uniform vec4 nameRect;
uniform float nameAlpha;
uniform vec4 melt[8];
uniform float meltR;
float hash(vec2 q) { return fract(sin(dot(q, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 q) {
  vec2 i = floor(q), f = fract(q); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}

float caustic(vec2 p, float t) {
  float w = sin(p.x * 3.1 + t * 0.55 + sin(p.y * 2.3 + t * 0.35))
          + sin(p.y * 3.7 - t * 0.45 + sin(p.x * 1.9 - t * 0.28))
          + sin((p.x + p.y) * 2.6 + t * 0.32 + 1.7 * noise(p * 0.8 + t * 0.05));
  return pow(clamp(1.0 - abs(w) / 1.6, 0.0, 1.0), 5.0);
}
vec3 water(vec2 q, float calm) {
  vec2 p = q * vec2(aspect, 1.0) * scale;
  float t = time * 0.35;
  float c = caustic(p, t) * 0.65 + caustic(p * 1.9 + 3.1, t * 1.3) * 0.35;

  float depth = noise(p * 0.35 + vec2(t * 0.06, -t * 0.04)) - 0.5;
  vec3 col = blue;
  col += vec3(0.30, 0.42, 0.40) * (c - 0.12) * 0.42 * glow * calm;
  col += vec3(0.02, 0.06, 0.07) * depth * calm;
  return col;
}
vec2 turn(vec2 v, float a) { float c = cos(a), s = sin(a); return vec2(c * v.x - s * v.y, s * v.x + c * v.y); }
void main() {
  vec2 h = view.xy + uv * view.zw;

  vec2 dv = (h - vortex.xy) * vec2(aspect, 1.0);
  float er = length(dv / vortexSize);
  float shape = exp(-er * er * 1.1);
  float fall = shape * vortex.z;

  float hl = texture2D(state, h - vec2(texel.x, 0.0)).r;
  float hr = texture2D(state, h + vec2(texel.x, 0.0)).r;
  float hd = texture2D(state, h - vec2(0.0, texel.y)).r;
  float hu = texture2D(state, h + vec2(0.0, texel.y)).r;
  vec3 n = normalize(vec3(hl - hr, hd - hu, 0.045));

  float calm = smoothstep(0.0, fade.x, h.y) * (fade.y > 0.0 ? smoothstep(0.0, fade.y, 1.0 - h.y) : 1.0);

  vec3 col = water(h + n.xy * 0.07, calm);
  if (vortex.z > 0.001) {

    float p1 = fract(vortex.w / 8.0);
    float p2 = fract(vortex.w / 8.0 + 0.5);
    float swing = 1.1 * shape;
    vec3 a = water(vortex.xy + turn(dv, shape * 0.5 + swing * p1) / vec2(aspect, 1.0) + n.xy * 0.07, calm);
    vec3 b = water(vortex.xy + turn(dv, shape * 0.5 + swing * p2) / vec2(aspect, 1.0) + n.xy * 0.07, calm);
    vec3 flowing = mix(a, b, abs(1.0 - 2.0 * p1));
    col = mix(col, flowing, smoothstep(0.0, 0.6, fall));
  }
  vec3 lightDir = normalize(vec3(-0.35 + tilt, 0.55, 1.0));
  float spec = pow(max(dot(reflect(-lightDir, n), vec3(0.0, 0.0, 1.0)), 0.0), 120.0);
  float shade = dot(n.xy, normalize(vec2(-0.45 + tilt, 0.65)));
  col += vec3(0.20, 0.30, 0.29) * shade * 0.45;
  col += vec3(0.90, 0.98, 0.98) * spec * 0.22;

  vec2 span = max(nameRect.zw - nameRect.xy, vec2(1e-4));
  vec2 nr = (h - nameRect.xy) / span;
  if (nameAlpha > 0.001 && nr.x > -0.1 && nr.x < 1.1 && nr.y > -0.7 && nr.y < 1.7) {
    float stir = vortex.z;
    vec2 sway = n.xy * 0.12 + vec2(sin(nr.y * 14.0 + time * 0.9), cos(nr.x * 11.0 + time * 0.7)) * 0.0022;
    vec2 at = nr + sway * stir;
    float a = texture2D(nameTex, vec2(at.x, 1.0 - at.y)).a;
    vec3 ink = inkColor;

    float m = 0.0;
    float held = 0.0;
    for (int i = 0; i < 8; i++) {
      vec4 s = melt[i];
      if (s.z <= 0.0) continue;
      vec2 d = (h - s.xy) * vec2(aspect, 1.0);
      float r = meltR * (1.0 + 0.3 * s.w);
      float g = exp(-dot(d, d) / (r * r)) * s.z;
      m += g;
      held += g * s.w;
    }
    held /= max(m, 1e-4);
    m = clamp(m, 0.0, 1.0);
    if (m > 0.003) {
      float hgt = span.y;
      vec2 q = h;
      float wash = 0.0;
      float total = 0.0;
      for (int k = 0; k < 7; k++) {
        vec2 gp = q * vec2(aspect, 1.0) / hgt * 2.2 + vec2(time * 0.07, -time * 0.05);
        float n0 = noise(gp);
        float nx = noise(gp + vec2(0.12, 0.0));
        float ny = noise(gp + vec2(0.0, 0.12));
        vec2 curl = vec2(ny - n0, n0 - nx) / 0.12;

        float reach = m * (0.7 + 0.45 * held);
        q -= curl * vec2(1.0 / aspect, 1.0) * hgt * 0.12 * reach;
        q.y += hgt * 0.04 * reach;
        vec2 qr = (q - nameRect.xy) / span;
        float w = 1.0 - float(k) * 0.11;
        wash += texture2D(nameTex, vec2(qr.x, 1.0 - qr.y)).a * w;
        total += w;
      }
      wash /= total;
      float spread = smoothstep(0.0, 0.55, m);

      a = mix(a, wash * (1.0 - min(0.85, 0.3 * m + 0.12 * held)), spread);
      ink = mix(ink, vec3(0.18, 0.42, 0.40), 0.4 * spread * (1.0 - wash));
    }
    col = mix(col, ink, a * nameAlpha);
  }
  gl_FragColor = vec4(col, 1.0);
}`;

type GL = WebGLRenderingContext | WebGL2RenderingContext;
interface Target {
  tex: WebGLTexture;
  fb: WebGLFramebuffer;
}
interface Program {
  program: WebGLProgram;
  u: Record<string, WebGLUniformLocation | null>;
}

const hex = (h: string): [number, number, number] => {
  const n = parseInt(h.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

function compile(gl: GL, type: number, src: string): WebGLShader {
  const s = gl.createShader(type);
  if (!s) throw new Error("shader");
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
    throw new Error(gl.getShaderInfoLog(s) ?? "compile");
  return s;
}

function program(gl: GL, frag: string, names: string[]): Program {
  const p = gl.createProgram();
  if (!p) throw new Error("program");
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, VERT));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, frag));
  gl.bindAttribLocation(p, 0, "p");
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS))
    throw new Error(gl.getProgramInfoLog(p) ?? "link");
  const u: Program["u"] = {};
  for (const n of names) u[n] = gl.getUniformLocation(p, n);
  return { program: p, u };
}

function floatFormat(gl: GL): { internal: number; format: number; type: number } | null {
  if ("RGBA16F" in gl) {
    if (!gl.getExtension("EXT_color_buffer_float")) return null;
    gl.getExtension("OES_texture_float_linear");
    return { internal: gl.RGBA16F, format: gl.RGBA, type: gl.HALF_FLOAT };
  }
  const half = gl.getExtension("OES_texture_half_float");
  gl.getExtension("OES_texture_half_float_linear");
  if (!half) return null;
  return { internal: gl.RGBA, format: gl.RGBA, type: half.HALF_FLOAT_OES };
}

interface WaterOptions {
  fade: [number, number];
  wake: boolean;
  name?: HTMLElement | null;
  scrollWake?: boolean;
}

interface Water {
  ring(clientX: number, clientY: number, strength: number, radius: number): void;
}

const touch = window.matchMedia("(pointer: coarse)").matches;

export function createWater(
  canvas: HTMLCanvasElement,
  host: HTMLElement,
  reduced: boolean,
  options: WaterOptions,
): Water | null {
  const opts = {
    antialias: false,
    alpha: true,
    premultipliedAlpha: false,
    preserveDrawingBuffer: false,
  };
  const gl = (canvas.getContext("webgl2", opts) ?? canvas.getContext("webgl", opts)) as GL | null;
  if (!gl) return null;
  const fmt = floatFormat(gl);
  if (!fmt) return null;

  let sim: Program, view: Program;
  try {
    sim = program(gl, SIM, ["state", "texel", "drops", "count", "aspect"]);
    view = program(gl, VIEW, [
      "state",
      "texel",
      "time",
      "tilt",
      "aspect",
      "blue",
      "inkColor",
      "glow",
      "view",
      "fade",
      "scale",
      "vortex",
      "vortexSize",
      "nameTex",
      "nameRect",
      "nameAlpha",
      "melt",
      "meltR",
    ]);
  } catch (err) {
    console.warn("water disabled:", err);
    return null;
  }

  const quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

  let simW = 0;
  let simH = 0;
  let aspect = 1;
  let screens = 1;
  let targets: Target[] = [];
  let cur = 0;

  const makeTarget = (w: number, h: number): Target | null => {
    const tex = gl.createTexture();
    const fb = gl.createFramebuffer();
    if (!tex || !fb) return null;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, fmt.internal, w, h, 0, fmt.format, fmt.type, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) return null;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    return { tex, fb };
  };

  const resize = (): boolean => {
    const rect = host.getBoundingClientRect();
    aspect = rect.width / Math.max(1, rect.height);
    screens = rect.height / Math.max(1, window.innerHeight);
    const h = Math.min(2048, Math.max(2, Math.round(220 * screens)));
    const w = Math.min(2048, Math.max(2, Math.round(h * aspect)));
    if (w === simW && h === simH) return true;
    targets.forEach((t) => {
      gl.deleteTexture(t.tex);
      gl.deleteFramebuffer(t.fb);
    });
    const a = makeTarget(w, h);
    const b = makeTarget(w, h);
    if (!a || !b) return false;
    targets = [a, b];
    simW = w;
    simH = h;
    cur = 0;
    return true;
  };
  if (!resize()) {
    console.warn("water disabled: float render target not supported");
    return null;
  }
  canvas.dataset.live = "true";

  const frame = { x: 0, y: 0, w: 1, h: 1 };
  const place = () => {
    const hr = host.getBoundingClientRect();
    const cr = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, touch ? 1 : 1.5);
    const cw = Math.max(2, Math.round(cr.width * dpr));
    const ch = Math.max(2, Math.round(cr.height * dpr));
    if (canvas.width !== cw) canvas.width = cw;
    if (canvas.height !== ch) canvas.height = ch;
    frame.x = (cr.left - hr.left) / hr.width;
    frame.y = 1 - (cr.bottom - hr.top) / hr.height;
    frame.w = cr.width / hr.width;
    frame.h = cr.height / hr.height;
  };

  const drops: [number, number, number, number][] = [];
  let lastDrop = -Infinity;
  const addDrop = (x: number, y: number, strength: number, radius: number) => {
    drops.push([x, y, strength, radius / screens]);
    if (drops.length > 48) drops.shift();
    lastDrop = performance.now();
  };
  const dropData = new Float32Array(32);
  const toUv = (clientX: number, clientY: number): [number, number] => {
    const r = host.getBoundingClientRect();
    return [(clientX - r.left) / r.width, 1 - (clientY - r.top) / r.height];
  };

  const step = () => {
    gl.useProgram(sim.program);
    gl.viewport(0, 0, simW, simH);
    gl.uniform2f(sim.u.texel ?? null, 1 / simW, 1 / simH);
    gl.uniform1f(sim.u.aspect ?? null, aspect);
    gl.uniform1i(sim.u.state ?? null, 0);
    const batch = drops.splice(0, drops.length);
    const passes = Math.max(1, Math.ceil(batch.length / 8));
    for (let i = 0; i < passes; i++) {
      const part = batch.slice(i * 8, i * 8 + 8);
      part.forEach((d, k) => dropData.set(d, k * 4));
      gl.uniform4fv(sim.u.drops ?? null, dropData);
      gl.uniform1i(sim.u.count ?? null, part.length);
      const next = targets[1 - cur] as Target;
      gl.bindFramebuffer(gl.FRAMEBUFFER, next.fb);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, (targets[cur] as Target).tex);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      cur = 1 - cur;
    }
  };

  const palette = { blue: hex("#84C3BE"), ink: hex("#293133"), glow: 1 };
  const readPalette = () => {
    const css = getComputedStyle(document.documentElement);
    const get = (name: string, fallback: string) => {
      const v = css.getPropertyValue(name).trim();
      return /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
    };
    palette.blue = hex(get("--surface", "#84C3BE"));
    palette.ink = hex(get("--water-ink", "#293133"));
    palette.glow = document.documentElement.dataset.theme === "dark" ? 1.6 : 1;
  };
  readPalette();
  window.addEventListener("otridon:theme", () => {
    readPalette();
    render(performance.now());
  });
  let tilt = 0;
  const name = options.name ?? null;
  const whirl = {
    target: 0,
    strength: 0,
    spin: 0,
    x: 0.5,
    y: 0.5,
    rx: 0.3,
    ry: 0.1,
  };
  const MELT = 8;
  const melt = Array.from({ length: MELT }, () => ({ x: 0, y: 0, heat: 0, held: 0 }));
  let meltR = 0.05;
  const meltData = new Float32Array(MELT * 4);
  let meltAt: (typeof melt)[number] | null = null;
  const nameText = name?.querySelector<HTMLElement>("[data-brand]") ?? name;
  const nameTex = gl.createTexture();
  const nameBox = { x0: 0, y0: 0, x1: 0, y1: 0 };
  let nameReady = false;
  let drawnSize = "";
  const placeName = () => {
    if (!nameText) return;
    const hr = host.getBoundingClientRect();
    const tr = nameText.getBoundingClientRect();
    if (nameReady && drawnSize !== `${Math.round(tr.width)}x${Math.round(tr.height)}`) drawName();
    nameBox.x0 = (tr.left - hr.left) / hr.width;
    nameBox.x1 = (tr.right - hr.left) / hr.width;
    nameBox.y0 = 1 - (tr.bottom - hr.top) / hr.height;
    nameBox.y1 = 1 - (tr.top - hr.top) / hr.height;
  };
  const drawName = () => {
    if (!nameText || !nameTex) return;
    const tr = nameText.getBoundingClientRect();
    if (tr.width < 2 || tr.height < 2) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const c = document.createElement("canvas");
    c.width = Math.ceil(tr.width * dpr);
    c.height = Math.ceil(tr.height * dpr);
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#fff";
    paintWordmark(ctx, tr.width, tr.height, 3);
    gl.bindTexture(gl.TEXTURE_2D, nameTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    drawnSize = `${Math.round(tr.width)}x${Math.round(tr.height)}`;
    nameReady = true;
    host.classList.add("has-live-name");
  };
  const placeWhirl = () => {
    if (!name) return;
    const hr = host.getBoundingClientRect();
    const nr = name.getBoundingClientRect();
    whirl.x = (nr.left + nr.width / 2 - hr.left) / hr.width;
    whirl.y = 1 - (nr.top + nr.height / 2 - hr.top) / hr.height;
    whirl.rx = (nr.width / hr.width) * aspect * 0.78;
    whirl.ry = (nr.height / hr.height) * 2.0;
  };
  const render = (time: number) => {
    place();
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.useProgram(view.program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, (targets[cur] as Target).tex);
    gl.uniform1i(view.u.state ?? null, 0);
    gl.uniform2f(view.u.texel ?? null, 1 / simW, 1 / simH);
    gl.uniform1f(view.u.time ?? null, time / 1000);
    gl.uniform1f(view.u.tilt ?? null, tilt);
    gl.uniform1f(view.u.aspect ?? null, aspect);
    gl.uniform3f(view.u.blue ?? null, ...palette.blue);
    gl.uniform3f(view.u.inkColor ?? null, ...palette.ink);
    gl.uniform1f(view.u.glow ?? null, palette.glow);
    gl.uniform4f(view.u.view ?? null, frame.x, frame.y, frame.w, frame.h);
    gl.uniform2f(view.u.fade ?? null, options.fade[0] / screens, options.fade[1] / screens);
    gl.uniform1f(view.u.scale ?? null, 2.4 * screens);
    gl.uniform4f(view.u.vortex ?? null, whirl.x, whirl.y, whirl.strength, whirl.spin);
    gl.uniform2f(view.u.vortexSize ?? null, Math.max(0.05, whirl.rx), Math.max(0.03, whirl.ry));
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, nameTex);
    gl.uniform1i(view.u.nameTex ?? null, 1);
    placeName();
    gl.uniform4f(view.u.nameRect ?? null, nameBox.x0, nameBox.y0, nameBox.x1, nameBox.y1);
    const alpha = nameReady && name ? Number(getComputedStyle(name).opacity) : 0;
    gl.uniform1f(view.u.nameAlpha ?? null, alpha);
    melt.forEach((m, i) => meltData.set([m.x, m.y, m.heat, m.held], i * 4));
    gl.uniform4fv(view.u.melt ?? null, meltData);
    gl.uniform1f(view.u.meltR ?? null, meltR);
    gl.activeTexture(gl.TEXTURE0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  const handle: Water = {
    ring: (clientX, clientY, strength, radius) => {
      if (reduced) return;
      const [x, y] = toUv(clientX, clientY);
      addDrop(x, y, strength, radius);
    },
  };

  new ResizeObserver(() => {
    resize();
    drawName();
    if (reduced) render(0);
  }).observe(host);
  void document.fonts.ready.then(() => {
    drawName();
    if (reduced) render(0);
  });

  if (reduced) {
    render(0);
    let queued = false;
    window.addEventListener(
      "scroll",
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          render(0);
        });
      },
      { passive: true },
    );
    return handle;
  }

  const onName = (e: PointerEvent) =>
    Boolean(name && e.target instanceof Node && name.contains(e.target));
  if (options.wake) {
    let last: [number, number] | null = null;
    host.addEventListener("pointermove", (e) => {
      if (onName(e)) return;
      const [x, y] = toUv(e.clientX, e.clientY);
      if (last && Math.hypot((x - last[0]) * aspect, y - last[1]) * screens < 0.012) return;
      last = [x, y];
      addDrop(x, y, -0.022, 0.03);
    });
    host.addEventListener("pointerleave", () => (last = null));
    host.addEventListener("pointerdown", (e) => {
      if (onName(e)) return;
      const [x, y] = toUv(e.clientX, e.clientY);
      addDrop(x, y, -0.06, 0.055);
    });
  }

  const meltOn = (e: PointerEvent) => {
    if (!name) return;
    const [x, y] = toUv(e.clientX, e.clientY);
    const hr = host.getBoundingClientRect();
    meltR = (name.getBoundingClientRect().height / hr.height) * 0.62;
    if (meltAt && Math.hypot((x - meltAt.x) * aspect, y - meltAt.y) < meltR * 0.5) return;
    const spot = melt.reduce((a, b) => (b.heat < a.heat ? b : a));
    Object.assign(spot, {
      x,
      y,
      heat: Math.max(spot.heat, 0.05),
      held: spot.heat > 0 ? spot.held : 0,
    });
    meltAt = spot;
    addDrop(x, y, -0.03, 0.035);
  };
  name?.addEventListener("pointermove", meltOn);
  name?.addEventListener("pointerleave", () => (meltAt = null));

  let release = 0;
  name?.addEventListener("pointerenter", (e) => {
    if (e.pointerType === "mouse") whirl.target = 1;
  });
  name?.addEventListener("pointerleave", (e) => {
    if (e.pointerType === "mouse") whirl.target = 0;
  });
  name?.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse") return;
    whirl.target = 1;
    window.clearTimeout(release);
    release = window.setTimeout(() => (whirl.target = 0), 3200);
  });

  const PRESS_TIME = 0.35;
  const presses: { v: number; total: number; age: number; seed: number }[] = [];
  const ease = (x: number) => x * x * (3 - 2 * x);
  let scrollPrev = window.scrollY;
  let scrollSpeed = 0;
  let travelled = Infinity;
  let direction = 0;
  let idle = 0;
  const startWave = (screenY: number, time: number) => {
    const r = host.getBoundingClientRect();
    if (screenY < r.top || screenY > r.bottom) return;
    travelled = 0;
    presses.push({
      v: 1 - (screenY - r.top) / r.height,
      total: -(0.13 + 0.06 * Math.min(1, scrollSpeed / 1500)),
      age: 0,
      seed: (time * 0.00013) % 1,
    });
  };
  const pressFromScroll = (dt: number, time: number) => {
    if (dt <= 0) return;
    const y = window.scrollY;
    const moved = y - scrollPrev;
    scrollPrev = y;
    scrollSpeed += (Math.abs(moved) / dt - scrollSpeed) * Math.min(1, dt * 6);
    if (moved === 0) {
      idle += dt;
      if (idle > 0.35) travelled = Infinity;
    } else {
      idle = 0;
      const dir = Math.sign(moved);
      if (dir !== direction) travelled = Infinity;
      direction = dir;
      travelled += Math.abs(moved);
      if (travelled >= window.innerHeight * 0.5)
        startWave(window.innerHeight * (dir > 0 ? 0.25 : 0.75), time);
    }
    for (let i = presses.length - 1; i >= 0; i--) {
      const p = presses[i] as (typeof presses)[number];
      const before = ease(Math.min(1, p.age / PRESS_TIME));
      p.age += dt;
      const after = ease(Math.min(1, p.age / PRESS_TIME));
      if (after > before) addDrop(p.seed, p.v, p.total * (after - before), -0.11);
      if (p.age >= PRESS_TIME) presses.splice(i, 1);
    }
  };

  let visible = true;
  let running = false;
  let lastTime = 0;
  const loop = (time: number) => {
    if (!visible) {
      running = false;
      lastTime = 0;
      return;
    }
    const dt = lastTime ? Math.min(0.05, (time - lastTime) / 1000) : 0;
    lastTime = time;
    const tau = whirl.target > whirl.strength ? 1.1 : 1.8;
    whirl.strength += (whirl.target - whirl.strength) * (1 - Math.exp(-dt / tau));
    if (whirl.strength > 0.01) whirl.spin += dt;
    if (whirl.strength > 0.001) placeWhirl();
    for (const m of melt) {
      if (m === meltAt) {
        m.heat = Math.min(1, m.heat + dt * 2.2);
        m.held = Math.min(6, m.held + dt * 0.8);
      } else {
        m.heat = Math.max(0, m.heat - dt * 0.28);
        m.held = m.heat > 0 ? Math.max(0, m.held - dt * 1.0) : 0;
      }
    }
    tilt = Math.min(0.35, window.scrollY / 2400);
    if (options.scrollWake) pressFromScroll(dt, time);
    if (drops.length || time - lastDrop < 30000) {
      const steps = Math.min(touch ? 2 : 4, Math.max(1, Math.round(dt * 60)));
      for (let s = 0; s < steps; s++) step();
    }
    render(time);
    requestAnimationFrame(loop);
  };
  const start = () => {
    if (running) return;
    running = true;
    requestAnimationFrame(loop);
  };
  new IntersectionObserver(([entry]) => {
    visible = Boolean(entry?.isIntersecting);
    if (visible) start();
  }).observe(host);

  start();
  return handle;
}

export function initWater(canvas: HTMLCanvasElement, host: HTMLElement, reduced: boolean): void {
  createWater(canvas, host, reduced, {
    fade: [0.32, 0],
    wake: true,
    name: host.querySelector<HTMLElement>("[data-whirl]"),
    scrollWake: true,
  });
}
